import React from 'react';
import { Box, ClickableBox, Flex, DesktopOnly } from '../ui';
import styled, { useTheme } from 'styled-components';
import Slider from 'components/ui/Slider';
import AutoHidingPanel from './AutoHidingPanel';
import { ReactComponent as MusicButton } from 'img/music-button.svg';
import { ReactComponent as PauseButton } from 'img/pause-button.svg';
import { ReactComponent as StopButton } from 'img/stop-button.svg'; 
import { ReactComponent as PlayButton } from 'img/play.svg';
import { ReactComponent as PreviousButton } from 'img/previous-button.svg';
import { ReactComponent as NextButton } from 'img/next-button.svg';
import { ReactComponent as FullscreenButton } from 'img/fullscreen.svg';
import { ReactComponent as ExitFullscreenButton } from 'img/exit-fullscreen.svg';
import { bpmControlEnabled } from 'utils/config';

const Stepper = styled.div`
  & > div {
    width: 0;
    height: 0;
    border-style: solid;
    cursor: pointer;
  }
`;

const Up = styled.div<any>`
  border-width: 0 10px 15px 10px;
  border-color: transparent transparent ${props => props.color} transparent;
  margin-bottom: 8px;
`;

const Down = styled.div<any>`
  border-width: 15px 10px 0 10px;
  border-color: ${props => props.color} transparent transparent transparent;
`;

const Tempo = styled(Box)`
  cursor: pointer;
`;

const TimeCurrent = styled(Box)`
  font-size: 12px;
  margin-right: 10px;
  @media (max-width: 480px) {
    margin-left: 10px;
  }
`;

const TimeDuration = styled(Box)`
  font-size: 12px;
  margin-left: 10px;
  @media (max-width: 480px) {
    margin-right: 10px;
  }
`;

interface PlayerControlsProps {
  activeBeat: any;
  title: string;
  isPlaying: boolean;
  onPrevious(): any;
  onToggle(): any;
  onNext(): any;
  playbackRate: number;
  onUpdatePlaybackRate(value: number): void;
  currentTimePercentage: number;
  currentTime: number;
  duration: number;
  onUpdateCurrentTimePercentage(value: number): void;
  chordsVisible: boolean;
  toggleChords(): void;
  isFullScreen?: boolean,
  toggleFullScreen?(): void;
  isFutura?: boolean;
}

const isSafari =
  navigator.vendor.match(/apple/i) &&
  !navigator.userAgent.match(/crios/i) &&
  !navigator.userAgent.match(/fxios/i);

const MIN_PLAYBACK_RATE = 0.5;
const MAX_PLAYBACK_RATE = isSafari ? 2.0 : 1.5;
const STEP = 0.1;

const PlayerControls = ({
  activeBeat,
  title,
  isPlaying,
  onPrevious,
  onToggle,
  onNext,
  playbackRate,
  onUpdatePlaybackRate,
  currentTimePercentage,
  currentTime,
  duration,
  onUpdateCurrentTimePercentage,
  chordsVisible,
  toggleChords,
  isFullScreen,
  toggleFullScreen,
  isFutura,
}: PlayerControlsProps) => {
  const { colors } = useTheme();

  const tempoUp = () => {
    if (playbackRate < MAX_PLAYBACK_RATE) {
      const newValue = parseFloat((playbackRate + STEP).toFixed(1));
      onUpdatePlaybackRate(newValue);
    }
  };

  const tempoDown = () => {
    if (playbackRate > MIN_PLAYBACK_RATE) {
      const newValue = parseFloat((playbackRate - STEP).toFixed(1));
      onUpdatePlaybackRate(newValue);
    }
  };

  const tempoReset = () => {
    onUpdatePlaybackRate(1);
  };

  const musicColor = chordsVisible ? colors.highlight : 'rgba(126, 126, 126, 0.4)';
  const maxWidth = isFullScreen ? 1200 : 360;

  return (
    <AutoHidingPanel isFullScreen={isFullScreen}>
      {
        isFullScreen && activeBeat && (
          <Box position="absolute" top={-60} my={3} fontSize={3} color={colors.highlight}>
            { title }
          </Box>
        )
      }
      <Flex width="100%" maxWidth={maxWidth} margin="0 auto 10px" alignItems="center" position="relative">
        {isFutura && !isFullScreen && (
          <Box
            color="highlight"
            fontSize="3"
            fontWeight="700"
            mt={['-12px', '-48px']}
            textAlign={['center', 'center', 'left']}
            width="100%"
          >
            {title}
          </Box>
        )}
        {
          !isFutura && (
            <DesktopOnly>
              <ClickableBox
                onClick={toggleFullScreen}
                position="absolute"
                top="-36px"
                right="0"
              >
                {
                  isFullScreen ? (
                    <ExitFullscreenButton title="Exit Fullscreen" fill={colors.highlight} width={24} />
                  ) : (
                    <FullscreenButton title="Fullscreen" fill={colors.highlight} width={24} />
                  ) 
                }
              </ClickableBox>
            </DesktopOnly>
          )
        }

        {!isFutura && (
          <>
            <TimeCurrent>{formatTime(currentTime)}</TimeCurrent>
            <Slider
              min={0}
              max={100}
              value={currentTimePercentage}
              onChange={onUpdateCurrentTimePercentage}
            />
            <TimeDuration>{formatTime(duration)}</TimeDuration>
          </>
        )}
        
      </Flex>

      <Flex maxWidth="360px" margin="0 auto" justifyBetween alignCenter>
        {isFutura ? <Box /> : (
          <ClickableBox onClick={toggleChords} zIndex={77}>
            <MusicButton title="Toggle Chords" fill={musicColor} stroke={musicColor} />
          </ClickableBox>
        )}

        <Flex>
          <ClickableBox ml={3} onClick={onPrevious} zIndex={77}>
            <PreviousButton fill={colors.highlight} stroke={colors.highlight} />
          </ClickableBox>

          <ClickableBox onClick={onToggle} disabled={!activeBeat} mx={2} zIndex={77}>
            {isPlaying ? (
              (isFutura ? <StopButton fill={colors.highlight} stroke={colors.highlight} />
                        : <PauseButton fill={colors.highlight} stroke={colors.highlight} />)
            ) : (
              <PlayButton fill={colors.highlight} stroke={colors.highlight} />
            )}
          </ClickableBox>

          <ClickableBox onClick={onNext} zIndex={77}>
            <NextButton fill={colors.highlight} stroke={colors.highlight} />
          </ClickableBox>
        </Flex>

        {bpmControlEnabled && !isFutura ? (
          <Box height="40px">
            <Flex alignItems="center" textAlign="center">
              <Box mr={1}>
                <Box color={colors.highlight} fontSize="11px" textAlign="left">
                  TEMPO
                </Box>
                <Tempo onClick={tempoReset} textAlign="center" fontSize="16px">
                  {playbackRate}x
                </Tempo>
              </Box>
              <Stepper>
                <Up
                  color={colors.highlight}
                  disabled={playbackRate === MIN_PLAYBACK_RATE}
                  onClick={tempoUp}
                />
                <Down
                  color={colors.highlight}
                  disabled={playbackRate === MAX_PLAYBACK_RATE}
                  onClick={tempoDown}
                />
              </Stepper>
            </Flex>
          </Box>
        ) : <Box />}
      </Flex>
    </AutoHidingPanel>
  );
};

const formatTime = (seconds: number) =>
  isNaN(seconds)
    ? ''
    : `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0')}`;

export default PlayerControls;
